import {
  SetStateAction,
  Dispatch,
  MouseEventHandler,
  FC,
  MouseEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  DevHubAlert,
  DevHubAutocomplete,
  DevHubBox,
  DevHubButton,
  DevHubLink,
  DevHubModal,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { UsersCloudAccountType } from '../types/CloudAccountTypes';
import {
  CreateCloudAccountMutation,
  DeleteCloudAccountMutation,
  GetApplicationsForCloudAccount,
  GetCloudAccountsAndApplicationsForUser,
} from './CloudAccountQueries';
import { UserContext } from '../../../contexts/UserContext';
import { Progress } from '@backstage/core-components';
import { DocumentNode } from 'graphql';
import { RemoveAssociation } from './RemoveAssociation';

type Props = {
  appName: string | undefined;
  applicationId: number;
  cloudAccountId: string;
  cloudAccountName: string;
  cloudAccountsQuery: DocumentNode;
  open: boolean;
  submitted: boolean;
  setSubmitted: Dispatch<SetStateAction<boolean>>;
  handleOpen: MouseEventHandler<HTMLButtonElement>;
  handleClose: MouseEventHandler<HTMLButtonElement>;
};

/**
 * This is a component that displays a modal to reassign a cloud account
 * @param props the props for the component
 */
export const UpdateCloudAccount: FC<Props> = (props: Props) => {
  const { login } = useContext(UserContext);
  const CREATE_APPLICATION_CONNECTION = CreateCloudAccountMutation;
  const DELETE_APPLICATION_CONNECTION = DeleteCloudAccountMutation;
  const [
    createApplicationConnection,
    { data: createData, loading: createLoading, error: createError },
  ] = useMutation(CREATE_APPLICATION_CONNECTION);
  const [
    deleteApplicationConnection,
    { data: deleteData, loading: deleteLoading, error: deleteError },
  ] = useMutation(DELETE_APPLICATION_CONNECTION);
  const [hasCreateError, setHasCreateError] = useState<boolean>(false);
  const [hasDeleteError, setHasDeleteError] = useState<boolean>(false);

  const [newApplicationId, setNewApplicationId] = useState<number | undefined>(
    undefined,
  );
  const [newApplicationName, setNewApplicationName] = useState<
    string | undefined
  >(undefined);

  const {
    data: userAccounts,
    error: userAccountsError,
    loading: userAccountsLoading,
  } = useQuery<UsersCloudAccountType>(GetCloudAccountsAndApplicationsForUser, {
    errorPolicy: 'all',
    variables: {
      login: login,
      filter: {
        havingRoles: ['owner', 'assistant_owner', 'approver'],
        applicationStatuses: ['Active', 'PreProd'],
      },
    },
  });

  const filteredApplications =
    userAccounts?.identity?.user?.resources?.applicationsPaged?.applications.filter(
      app => app.applicationId !== props.applicationId,
    );

  const { data: applicationsData } = useQuery(GetApplicationsForCloudAccount, {
    variables: { cloudAccountIds: [props.cloudAccountId] },
  });

  const applications =
    (applicationsData as any)?.devhub?.connections?.cloudAccounts?.[0]
      ?.applications || [];
  const canDeleteCloudAccount = applications.length > 1;

  const handleReassign = async () => {
    await createApplicationConnection({
      variables: {
        input: {
          applicationId: newApplicationId,
          cloudAccountIds: props.cloudAccountId,
        },
      },
    });

    await deleteApplicationConnection({
      variables: {
        input: {
          applicationId: props.applicationId,
          cloudAccountId: props.cloudAccountId,
        },
      },
    });
  };

  useEffect(() => {
    if (createError) {
      setHasCreateError(true);
    }
    if (deleteError) {
      setHasDeleteError(true);
    }
  }, [createError, deleteError]);

  const isSuccessfullyReassigned =
    createData?.devhub?.connections?.cloudAccount?.addApplicationConnection
      ?.success &&
    deleteData?.devhub?.connections?.cloudAccount?.removeApplicationConnection
      ?.success;

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    setNewApplicationId(undefined);
    setNewApplicationName(undefined);
    setHasCreateError(false);
    setHasDeleteError(false);
    props.handleClose(event);

    if (
      createData?.devhub?.connections?.cloudAccount?.addApplicationConnection
        ?.success
    ) {
      window.location.reload();
    }
  };

  if (userAccountsLoading) {
    return <Progress />;
  } else if (userAccountsError) {
    return (
      <DevHubAlert severity="error">
        Error loading cloud account data
      </DevHubAlert>
    );
  }

  if (canDeleteCloudAccount) {
    return (
      <RemoveAssociation
        appName={props?.appName}
        applicationId={props.applicationId}
        cloudAccountName={props.cloudAccountName}
        cloudAccountId={props.cloudAccountId}
        cloudAccountsQuery={props.cloudAccountsQuery}
        submitted={props.submitted}
        setSubmitted={props.setSubmitted}
        handleOpen={props.handleOpen}
        handleClose={props.handleClose}
        open={props.open}
      />
    );
  }

  return (
    <DevHubModal open={props.open} onClose={props.handleClose}>
      <DevHubBox mt={2}>
        {isSuccessfullyReassigned ? (
          <>
            <DevHubAlert severity="success">
              Cloud account <strong>{props.cloudAccountName}</strong>{' '}
              successfully reassigned to{' '}
              <DevHubLink
                href={`/app/id/${newApplicationId}/cloud-accounts`}
                target="_blank"
              >
                {newApplicationName}
              </DevHubLink>
            </DevHubAlert>
            <DevHubBox display="flex" justifyContent="center" m={2}>
              <DevHubButton
                height="medium"
                variant="contained"
                onClick={handleClose}
              >
                Close & Refresh
              </DevHubButton>
            </DevHubBox>
          </>
        ) : (
          <>
            <DevHubTypography paragraph>
              A cloud account must be linked to at least one active application.
              Kindly reassign this cloud account to a different application to
              discontinue this association. If your intention is to decommission
              the account instead, please initiate the decommissioning process
              by following{' '}
              <DevHubLink
                target="_blank"
                href="https://docs-dx.comcast.com/kb/data-management/additional-resources-data-management/data-decommission-guidelines/"
              >
                this link.
              </DevHubLink>
            </DevHubTypography>
            <DevHubTypography variant="h6">
              Reassign {props?.cloudAccountName}
            </DevHubTypography>
            <DevHubAutocomplete
              loading={userAccountsLoading}
              loadingText="Loading.."
              options={filteredApplications || []}
              onChange={(_, value) => {
                setNewApplicationId(value?.applicationId);
                setNewApplicationName(value?.name);
              }}
              getOptionLabel={option => option.name}
              renderInput={params => (
                <DevHubTextField
                  {...params}
                  label="Select an application"
                  margin="normal"
                  helperText="Non-retired Application Selection"
                  variant="outlined"
                  required
                  InputProps={params.InputProps}
                />
              )}
            />
            <DevHubBox display="flex" justifyContent="space-between" mb={2}>
              <DevHubButton
                height="medium"
                variant="contained"
                onClick={handleClose}
              >
                Cancel
              </DevHubButton>
              <DevHubButton
                height="medium"
                variant="contained"
                buttonColor="green"
                onClick={handleReassign}
                disabled={createLoading || deleteLoading || !newApplicationId}
              >
                Submit Reassignment
              </DevHubButton>
            </DevHubBox>
            {createLoading || (deleteLoading && <Progress />)}
            {hasCreateError && (
              <DevHubTypography color="error">
                Create Error: {createError?.message}
              </DevHubTypography>
            )}
            {hasDeleteError && (
              <DevHubTypography color="error">
                Delete Error: {deleteError?.message}
              </DevHubTypography>
            )}
          </>
        )}
      </DevHubBox>
    </DevHubModal>
  );
};
